import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../../lib/api/settings';
import { NETWORKS } from '../../lib/networks';
import MenuItems from './MenuItems';
import MenuItemsOnePage from './MenuItemsOnePage';
import Logo from '../../assets/img/logo/sanasa-logo-dark.png';
import OffCanvasInsta from '../OffCanvas';

// Navbar 4: a contact bar on top (phone, email, address, social links from the back office settings),
// then a white menu bar with the Apply Now button. The menu bar sticks to the top after scrolling.
const HeaderFour = (props) => {
  const { contact } = useSiteSettings();
  const { headerClass, headerLogo, onePage, parentMenu } = props;
  const [isVisible, setIsVisible] = useState(false);
  const [isOffCanvasOpen, setIsOffCanvasOpen] = useState(false);
  const [holderHeight, setHolderHeight] = useState(null);
  const headerRef = useRef(null);
  const holderRef = useRef(null);
  const stuck = useRef(false);
  const social = NETWORKS.filter(([key]) => contact[key]);

  useEffect(() => {
    // Sticky header appears after scrolling 400 pixels. The menu bar leaves the page flow then, so its space is kept
    // (otherwise the page would jump up by the height of the bar).
    const toggleVisibility = () => {
      const past = window.scrollY > 400;
      if (past === stuck.current) return;
      if (past && holderRef.current) setHolderHeight(holderRef.current.offsetHeight);
      stuck.current = past;
      setIsVisible(past);
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  useEffect(() => {
    // Pages that fill the screen (the home banner) subtract the header height, which depends on the screen size.
    const header = headerRef.current;
    if (!header) return undefined;
    const publish = () => document.documentElement.style.setProperty('--sc-header-h', `${header.offsetHeight}px`);
    publish();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', publish);
      return () => window.removeEventListener('resize', publish);
    }
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <header ref={headerRef} className={headerClass ? headerClass : 'sc-header'}>
        <div className="sc-topbar">
          <div className="container">
            <div className="sc-topbar__row">
              <ul className="sc-topbar__info">
                <li>
                  <a href={contact.generalPhoneHref}>
                    <i className="fal fa-phone-alt"></i>
                    {contact.generalPhone}
                  </a>
                </li>
                <li className="d-none d-md-block">
                  <a href={`mailto:${contact.email}`}>
                    <i className="fal fa-envelope"></i>
                    {contact.email}
                  </a>
                </li>
                <li className="d-none d-lg-block">
                  <Link to="/contact">
                    <i className="fal fa-map-marker-alt"></i>
                    {contact.address}
                  </Link>
                </li>
              </ul>
              {social.length > 0 && (
                <div className="sc-topbar__social d-none d-md-flex">
                  {social.map(([key, label, icon]) => (
                    <a key={key} href={contact[key]} target="_blank" rel="noreferrer" aria-label={label}>
                      <i className={icon}></i>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sc-nav-holder" ref={holderRef} style={isVisible && holderHeight ? { height: holderHeight } : undefined}>
          <div id="header-sticky" className={isVisible ? 'sc-nav header-sticky' : 'sc-nav'}>
            <div className="container">
              <div className="sc-nav__wrap">
              <div className="row align-items-center">
                <div className="col-xl-3 col-lg-6 col-md-6 col-6">
                  <div className="sc-nav__logo">
                    <Link to="/">
                      <img src={headerLogo ? headerLogo : Logo} alt="SANASA Campus Ltd." width="200" />
                    </Link>
                  </div>
                </div>
                <div className="col-xl-6 d-none d-xl-block">
                  <div className="ed-header-5-main-menu it-main-menu">
                    <nav className="it-menu-content">
                      {!onePage ? <MenuItems /> : <MenuItemsOnePage parentMenu={parentMenu} onePageStyle="onePage4" />}
                    </nav>
                  </div>
                </div>
                <div className="col-xl-3 col-lg-6 col-md-6 col-6">
                  <div className="sc-nav__right d-flex align-items-center justify-content-end">
                    <div className="d-none d-md-block">
                      <Link className="ed-btn-radius" to="/apply">
                        Apply Now
                      </Link>
                    </div>
                    <div className="d-xl-none">
                      <button className="it-menu-bar ml-25" aria-label="Open menu" onClick={() => setIsOffCanvasOpen(true)}>
                        <svg width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10 18.3333C10 17.4128 10.7462 16.6667 11.6667 16.6667H21.6667C22.5872 16.6667 23.3333 17.4128 23.3333 18.3333C23.3333 19.2538 22.5872 20 21.6667 20H11.6667C10.7462 20 10 19.2538 10 18.3333ZM0 1.66667C0 0.746183 0.746183 0 1.66667 0H21.6667C22.5872 0 23.3333 0.746183 23.3333 1.66667C23.3333 2.58713 22.5872 3.33333 21.6667 3.33333H1.66667C0.746183 3.33333 0 2.58713 0 1.66667ZM0 10C0 9.07953 0.746183 8.33333 1.66667 8.33333H21.6667C22.5872 8.33333 23.3333 9.07953 23.3333 10C23.3333 10.9205 22.5872 11.6667 21.6667 11.6667H1.66667C0.746183 11.6667 0 10.9205 0 10Z"
                            fill="currentColor"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="it-offcanvas-area">
        <div className={isOffCanvasOpen ? 'itoffcanvas opened' : 'itoffcanvas'}>
          <div className="it-offcanva-bottom-shape d-none d-xxl-block"></div>
          <div className="itoffcanvas__close-btn">
            <button className="close-btn" onClick={() => setIsOffCanvasOpen(false)}>
              <i className="fal fa-times"></i>
            </button>
          </div>
          <div className="itoffcanvas__logo">
            <Link to="/">
              <img src={Logo} alt="SANASA Campus Ltd." width="240" />
            </Link>
          </div>
          <div className="itoffcanvas__text">
            <p>SANASA Campus is the first co-operative degree-awarding university institute of Sri Lanka, in Kegalle.</p>
          </div>
          <div className="it-menu-mobile d-xl-none">
            {!onePage ? <MenuItems mobileMenu="show" /> : <MenuItemsOnePage parentMenu={parentMenu} onePageStyle="onePage4" mobileMenu="show" />}
          </div>
          <div className="itoffcanvas__info">
            <h3 className="offcanva-title">Get In Touch</h3>
            <div className="it-info-wrapper mb-20 d-flex align-items-center">
              <div className="itoffcanvas__info-icon">
                <a href="#">
                  <i className="fal fa-envelope"></i>
                </a>
              </div>
              <div className="itoffcanvas__info-address">
                <span>Email</span>
                <a href={`mailto:${contact.email}`}>{contact.email}</a>
              </div>
            </div>
            <div className="it-info-wrapper mb-20 d-flex align-items-center">
              <div className="itoffcanvas__info-icon">
                <a href="#">
                  <i className="fal fa-phone-alt"></i>
                </a>
              </div>
              <div className="itoffcanvas__info-address">
                <span>Phone</span>
                <a href={contact.generalPhoneHref}>{contact.generalPhone}</a>
              </div>
            </div>
            <div className="it-info-wrapper mb-20 d-flex align-items-center">
              <div className="itoffcanvas__info-icon">
                <a href="#">
                  <i className="fas fa-map-marker-alt"></i>
                </a>
              </div>
              <div className="itoffcanvas__info-address">
                <span>Location</span>
                <span>{contact.address}</span>
              </div>
            </div>
          </div>
          <OffCanvasInsta />
        </div>
      </div>

      {isOffCanvasOpen ? (
        <div className="body-overlay apply" onClick={() => setIsOffCanvasOpen(false)}></div>
      ) : (
        <div className="body-overlay"></div>
      )}
    </>
  );
};
export default HeaderFour;
